
import { computed, defineComponent, ref, watch, PropType, getCurrentInstance } from 'vue'
import get from 'lodash/get'
import clamp from 'lodash/clamp'
import { Audience, AudienceSettingsSchemaItem, ActivationToEdit } from '../../../../../types/GCB2'
import {
    AUDIENCE_BOOLEAN_SELECT_DEFAULT_LIST,
    CHANGE_ACTIVATION_TYPES,
} from '../../../../../vars/GCB2'
import TextField from '../../../../inputs/TextField.vue'
import AudienceNumberInput from './AudienceNumberInput.vue'
import AudienceDateInput from './AudienceDateInput.vue'
import AudienceSelect from './AudienceSelect.vue'
import AudienceSettingsBaseInput from '@/components/inputs/AudienceSettingsBaseInput.vue'
import CustomActivatorSelect from '../../../../inputs/CustomActivatorSelect.vue'
import CustomActivatorTreeSelect from '../../../../inputs/CustomActivatorTreeSelect.vue'
import { declOfNum } from '../../../../../utils'
 import AudienceTextInput from './AudienceTextInput.vue'
 import cfg from '@root/config'
 import BaseInputOutlined from '@root/src/components/inputs/BaseInputOutlined.vue'
 import SmallHint from '@/components/SmallHint.vue'
 import AudienceSchemaInlineFields from './AudienceSchemaInlineFields.vue'
 import AudienceSchemaBlockCard from './AudienceSchemaBlockCard.vue'
import { resetAudienceBlockValues } from './audienceBlockChanged'
import { getMassEditClipboardKey, readMassEditClipboard } from './audienceMassEditClipboard'
import { buildMailingAdditionalSettingsGroups } from './audienceMailingSettings'
 import AudienceMailingAdditionalSettings from './AudienceMailingAdditionalSettings.vue'
import AudienceInformNote from './AudienceInformNote.vue'

export default defineComponent({
    components: {
        TextField,
        AudienceNumberInput,
        AudienceSelect,
        AudienceSettingsBaseInput,
        CustomActivatorSelect,
        CustomActivatorTreeSelect,
         AudienceDateInput,
         AudienceTextInput,
         BaseInputOutlined,
         SmallHint,
         AudienceSchemaInlineFields,
         AudienceSchemaBlockCard,
         AudienceMailingAdditionalSettings,
         AudienceInformNote,
    },
    props: {
        value: { type: Object, required: true },
        audience: { type: Object as PropType<Audience>, required: true },
        lists: { type: Object, required: true },
        mode: { type: String, required: true },
        type: { type: String, required: false },
        massEdit: { type: Boolean, required: false, default: false },
        currentActivation: { type: Object as PropType<ActivationToEdit>, required: false },
    },
    setup(props, { emit }) {
        const root = getCurrentInstance()!.proxy
        const filterValue = ref({})
        const activationType = computed(() =>{
            return root.$router.currentRoute.query.type
        } )
        const getBooleanSelectList = (schemaItem: AudienceSettingsSchemaItem) => {
            return [
                { id: true, name: schemaItem.trueText ?? AUDIENCE_BOOLEAN_SELECT_DEFAULT_LIST.trueText },
                { id: false, name: schemaItem.falseText ?? AUDIENCE_BOOLEAN_SELECT_DEFAULT_LIST.falseText },
            ]
        }
        const audience = props.audience
        const isParameterized = computed(() => audience?.settings.parameterized)
        const schema = computed(() => {
            if (props.type === 'preview') {
                return audience?.settings.shortSchema
            }
            return audience?.settings.schema
        })
        const mailingAdditionalSettingsGroups = computed(() =>
            props.type === 'preview' ? [] : buildMailingAdditionalSettingsGroups(audience, props.mode),
        )
        const mailingListStart = computed(() => {
            const s = schema.value
            return (Array.isArray(s) ? s.length : 0) + 1
        })
        const informText = computed(() => audience?.settings?.informText || '')
        const deselectAudience = () => {
            emit('select-audience', null)
        }
        const getComponentType = (schemaItem: AudienceSettingsSchemaItem) => {
            if (schemaItem.type === 'select' && schemaItem.list === 'service_list') {
                return 'treeSelect'
            }
            if (schemaItem.type === 'select' || schemaItem.type === 'boolSelect') {
                return 'select'
            }
            if (schemaItem.type === 'text') {
                return 'text'
            }
            if (schemaItem.type === 'date') {
                return 'date'
            }
            if (schemaItem.type === 'numberInput') {
                return 'numberInput'
            }
            if (schemaItem.type === 'textInput') {
                return 'textInput'
            }
            if (schemaItem.type === 'block') {
                return 'block'
            }
            if (schemaItem.type === 'popupHint') {
                return 'popupHint'
            }
            if (schemaItem.type === 'dropdownHint') {
                return 'dropdownHint'
            }
            if (schemaItem.type === 'toggleBlock') {
                return 'toggleBlock'
            }
            if (schemaItem.type === 'collapsibleBlock') {
                return 'collapsibleBlock'
            }
        }
        const getInputWidth = (item: AudienceSettingsSchemaItem) => {
            return clamp(item.width ?? 0, 70, Number.MAX_SAFE_INTEGER)
        }
        const getList = (item: AudienceSettingsSchemaItem) => {
            if (item.type === 'boolSelect') {
                return getBooleanSelectList(item)
            }
            if (item.list) {
                return props.lists[item.list as string]
            }
            if (item.customList) {
                return item.customList
            }
        }
        const getInputValue = (item: AudienceSettingsSchemaItem, selected: any) =>
            item.multiple ? `${item.label} (${selected.length})` : selected?.name ?? ''
        const getShowTooltip = (item: AudienceSettingsSchemaItem) => get(item, 'showTooltip')

        const dropdownHintsState = ref<Record<string | number, boolean>>({})
        const isDropdownHintOpen = (id?: string | number) => (id != null ? !!dropdownHintsState.value[id] : false)
        const toggleDropdownHint = (id?: string | number) => {
            if (id == null) {
                return
            }
            const current = dropdownHintsState.value
            const next: Record<string | number, boolean> = { ...current }
            next[id] = !current[id]
            dropdownHintsState.value = next
        }

        const blockItems = computed(() => {
            const result: AudienceSettingsSchemaItem[] = []
            const raw: any = schema.value

            if (!Array.isArray(raw)) {
                return result
            }

            raw.forEach((part: any) => {
                if (Array.isArray(part)) {
                    part.forEach((item: any) => {
                        if (item && item.type === 'block') {
                            result.push(item as AudienceSettingsSchemaItem)
                        }
                    })
                }
            })

            return result
        })

        const isToggleBlockVisible = (item: AudienceSettingsSchemaItem, audienceValues: Record<string, any>) => {
            const current = audienceValues[item.toggle as string]
            const expected = item.toggleValue
            if (Array.isArray(expected)) {
                return expected.includes(current)
            }
            return current === expected
        }

        const getVisibleBlockSchemaItems = (blockSchema?: AudienceSettingsSchemaItem[]) => {
            const audienceValues = props.value[audience?.id] || {}
            const flatten = (items?: AudienceSettingsSchemaItem[]): AudienceSettingsSchemaItem[] => {
                if (!Array.isArray(items)) {
                    return []
                }
                const result: AudienceSettingsSchemaItem[] = []
                items.forEach((inner) => {
                    if (!inner) {
                        return
                    }
                    if (Array.isArray(inner)) {
                        result.push(...flatten(inner))
                        return
                    }
                    if (inner.type === 'toggleBlock') {
                        if (isToggleBlockVisible(inner, audienceValues)) {
                            result.push(...flatten(inner.content))
                        }
                        return
                    }
                    result.push(inner)
                })
                return result
            }
            return flatten(blockSchema)
        }

        const resetBlock = (blockItem: AudienceSettingsSchemaItem) => {
            const audienceId = props.audience?.id
            if (!blockItem || audienceId == null) {
                return
            }
            if (!props.value[audienceId]) {
                root.$set(props.value, audienceId, {})
            }
            resetAudienceBlockValues(blockItem, props.value[audienceId], (target, key, val) => {
                root.$set(target, key, val)
            })
        }

        const disableBlock = (blockItem: AudienceSettingsSchemaItem) => {
            if (!blockItem || !blockItem.valueField) {
                return
            }
            if (!props.value[audience?.id]) {
                return
            }
            props.value[audience?.id][blockItem.valueField] = false
        }

        const enabledBlocks = computed({
            get: () =>
                blockItems.value
                    .filter((item: AudienceSettingsSchemaItem) => props.value[audience?.id]?.[item.valueField!])
                    .map((item: AudienceSettingsSchemaItem) => item.valueField),
            set: (selected: string[]) => {
                blockItems.value.forEach((item: AudienceSettingsSchemaItem) => {
                    props.value[audience?.id][item.valueField!] = selected.includes(item.valueField!)
                })
            },
        })
        const blockOpenState = ref<Record<string, boolean>>({})
        const isBlockOpen = (valueField?: string) => {
            if (!valueField) {
                return false
            }
            if (Object.prototype.hasOwnProperty.call(blockOpenState.value, valueField)) {
                return blockOpenState.value[valueField]
            }
            return props.mode === CHANGE_ACTIVATION_TYPES.create
        }
        const toggleBlockOpen = (valueField?: string) => {
            if (!valueField) {
                return
            }
            blockOpenState.value = { ...blockOpenState.value, [valueField]: !isBlockOpen(valueField) }
        }
        const firstEnabledBlockField = computed(() => {
            const enabled = new Set((enabledBlocks.value || []).filter(Boolean) as string[])
            if (!enabled.size) {
                return null
            }
            const raw: any = schema.value
            if (!Array.isArray(raw)) {
                return null
            }
            for (const part of raw) {
                const items = Array.isArray(part) ? part : []
                for (const item of items) {
                    if (item?.type === 'block' && item.valueField && enabled.has(item.valueField)) {
                        return item.valueField
                    }
                }
            }
            return null
        })
        const expandAllBlocks = () => {
            const next: Record<string, boolean> = { ...blockOpenState.value }
            ;(enabledBlocks.value || []).forEach((field) => {
                if (field) {
                    next[field] = true
                }
            })
            blockOpenState.value = next
        }
        const collapseAllBlocks = () => {
            const next: Record<string, boolean> = { ...blockOpenState.value }
            ;(enabledBlocks.value || []).forEach((field) => {
                if (field) {
                    next[field] = false
                }
            })
            blockOpenState.value = next
        }
        const areAllBlocksOpen = computed(() => {
            const fields = (enabledBlocks.value || []).filter(Boolean) as string[]
            return fields.length > 0 && fields.every((field) => isBlockOpen(field))
        })
        const toggleAllBlocks = () => {
            if (areAllBlocksOpen.value) {
                collapseAllBlocks()
            } else {
                expandAllBlocks()
            }
        }
        watch(
            () => (enabledBlocks.value || []).slice(),
            (next: Array<string | undefined>, prev: Array<string | undefined> | undefined) => {
                if (!prev) {
                    return
                }
                next.forEach((field) => {
                    if (field && !prev.includes(field)) {
                        blockOpenState.value = { ...blockOpenState.value, [field]: true }
                    }
                })
            },
        )
        const copy = (valueField: string, list: any[], selectType: 'default' | 'tree', listKey?: string) => {
            const clearName = getMassEditClipboardKey(valueField, listKey)
            const value = props.value[audience?.id][valueField] || []
            let updatedValue = []
            if(selectType === 'tree') {
                const flatList = []
                list.forEach(el => flatList.push(...el.children))
                const filterValue = flatList.filter(el => value.includes(el.id))
                updatedValue = filterValue
            } else {
                const filterValue = list.filter(el => value.includes(el.id))
                updatedValue = filterValue
            }
            const obj = {
                name: props.currentActivation?.name,
                projectId: props.currentActivation?.projectId,
                value: updatedValue
            }
            const string = JSON.stringify(obj)
            localStorage.setItem('massEdit-' + clearName, string)
            root.$store.dispatch('callNotify', `Скопировано в буфер обмена`)
        }
        const paste = (valueField: string, list: any[], selectType: 'default' | 'tree', listKey?: string) => {
            const string = readMassEditClipboard(valueField, listKey)
            if(string) {
                const json = JSON.parse(string)
                const clearValueNames = json.value.filter(el => el.name).map(el => el.name.toLowerCase())
                if (json.projectId === props.currentActivation?.projectId) {
                    props.value[audience?.id][valueField] = json.value.map(el => el.id)
                } else {
                    if(selectType === 'tree') {
                        const flatList = []
                        list.forEach(el => flatList.push(...el.children))
                        const clearList = flatList.map(el => {
                            return {
                                ...el,
                                name: el.name.toLowerCase()
                            }
                        })
                        const filterValue = clearList.filter(el => clearValueNames.includes(el.name))
                        props.value[audience?.id][valueField] = filterValue.map(el => el.id)
                    } else {
                        const clearList = list.map(el => {
                            return {
                                ...el,
                                name: el.name.toLowerCase()
                            }
                        })
                        const filterValue = clearList.filter(el => clearValueNames.includes(el.name))
                        props.value[audience?.id][valueField] = filterValue.map(el => el.id)
                    }
                }                
            } else {
                root.$store.dispatch('callNotify', `Нет скопированных данных`)
            }
        }

        return {
            isParameterized,
            schema,
            filterValue,
            getBooleanSelectList,
            getComponentType,
            getInputWidth,
            getList,
            getInputValue,
            declOfNum,
            deselectAudience,
            informText,
            mailingAdditionalSettingsGroups,
            mailingListStart,
            getShowTooltip,
            CHANGE_ACTIVATION_TYPES,
            activationType,
            cfg,
            copy,
            paste,
            blockItems,
            enabledBlocks,
            resetBlock,
            disableBlock,
            isBlockOpen,
            toggleBlockOpen,
            firstEnabledBlockField,
            expandAllBlocks,
            areAllBlocksOpen,
            toggleAllBlocks,
            dropdownHintsState,
            isDropdownHintOpen,
            toggleDropdownHint,
            getVisibleBlockSchemaItems,
        }
    },
})
